import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import useOnClickOutside from "use-onclickoutside";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import {useToasts} from "react-toast-notifications";

import * as actionTypes from "../../../store/actions/actionTypes";
import {linkBookingPreAdmission, loadBookingPreAdmissionHistory} from "../../../store/actions/bookingList";

const customStyles = {
    menu: (provided) => ({...provided, zIndex: '1000'}),
};

const animatedComponents = makeAnimated();

/**
 * Booking List "PA" column modal: mirrors the admission-side PreAdmissionsSet flow, but writes the
 * chosen pre-admission id onto the Booking (Booking::preAdmission_id) instead of an Admission.
 */
const BookingPreAdmissionSet = () => {
    const dispatch = useDispatch();
    const {addToast} = useToasts();
    const {show, booking, customUiId} = useSelector(state => state.modal.bookingPreAdmissionSet);
    const ref = React.useRef(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [options, setOptions] = useState([]);
    const [preAdmissionId, setPreAdmissionId] = useState(null);
    const isOptionsLoaded = options.length > 0;

    const hideModal = () => dispatch({type: actionTypes.MODAL_BOOKING_PRE_ADMISSION_SET_HIDE});
    useOnClickOutside(ref, () => hideModal());

    // Reset transient state whenever a new booking is opened.
    useEffect(() => {
        if (show) {
            setLoading(false);
            setError('');
            setOptions([]);
            setPreAdmissionId(null);
        }
    }, [show, booking?.id]);

    const doLoadHistory = () => {
        setLoading(true);
        setError('');
        dispatch(loadBookingPreAdmissionHistory(booking, customUiId))
            .then((data) => {
                setLoading(false);
                if (!data || data.error) {
                    setError(data?.error || 'Failed to load pre-admissions');
                    return;
                }
                const results = data.results || [];
                const _options = results.map(item => ({
                    value: item.id,
                    label: `${item.firstName} ${item.lastName} - ${item.admissionDate} (DOB ${item.dob})${item.mobile ? ` - ${item.mobile}` : ''}`,
                }));
                // Pre-select the candidate whose surname matches the booking (remote search is date-only).
                const _match = results.find(item => (item.lastName || '').toLowerCase() === (booking.surname || '').toLowerCase());
                if (_match) {
                    setPreAdmissionId(_match.id);
                }
                setOptions(_options);
            })
            .catch(() => {
                setLoading(false);
                setError('Failed to load pre-admissions');
            });
    };

    const doLink = () => {
        setLoading(true);
        dispatch(linkBookingPreAdmission(booking, preAdmissionId, addToast));
    };

    const renderForm = () => {
        if (isOptionsLoaded) {
            const _value = preAdmissionId ? options.find(item => item.value === preAdmissionId) : null;
            return (
                <>
                    <Select
                        options={options}
                        styles={customStyles}
                        isMulti={false}
                        components={animatedComponents}
                        value={_value}
                        onChange={(option) => setPreAdmissionId(option.value)}
                    />
                    <br/>
                    <div className={`form-group ${error ? 'form-error' : ''}`}>
                        <input type="button" value="Link pre-admission" className="btn btn-primary"
                               disabled={loading || !preAdmissionId} onClick={doLink}/>
                    </div>
                </>
            );
        }
        return (
            <div className={`form-group ${error ? 'form-error' : ''}`}>
                <input type="button" value="Load pre-admissions history" className="btn btn-primary"
                       disabled={loading} onClick={doLoadHistory}/>
            </div>
        );
    };

    const renderModal = () => (
        <div className="modal-dialog modal-content-large" role="document" ref={ref}>
            <div className="modal-content modal-content-fit">
                <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={hideModal}>
                    <span aria-hidden="true">×</span>
                </button>
                <div className="modal-body">
                    <h3>{booking.firstName} {booking.surname}</h3>
                    <dl className="person-list mb-3">
                        <dt>Surgeon</dt>
                        <dd>{booking.surgeon}</dd>
                        <dt>Mobile</dt>
                        <dd>{booking.phone || <span className="text-danger">empty</span>}</dd>
                        <dt>Procedure Date</dt>
                        <dd>{booking.procedureDate}</dd>
                    </dl>
                    <h4>Select pre-admission</h4>
                    {error && <p className="error">{error}</p>}
                    {renderForm()}
                </div>
            </div>
        </div>
    );

    return (
        <div className={`modal fade ${show ? 'show' : ''}`} id="bookingPreAdmissionModal" tabIndex="-1" role="dialog" aria-modal="true">
            {show && renderModal()}
        </div>
    );
};

export default BookingPreAdmissionSet;
